import { DateTime } from "luxon";
import { lang } from "../../../middleware/lang";

export type DatePreset = {
  key: string;
  label: string;
  getRange(): [Date, Date];
};

export type DatePresetGroup = {
  key: string;
  label: string;
  presets: DatePreset[];
};

function today(): DateTime {
  return DateTime.local().startOf("day");
}

function range(start: DateTime, end: DateTime): [Date, Date] {
  return [start.toJSDate(), end.toJSDate()];
}

// Luxon weekday: 1 = Monday ... 7 = Sunday
function startOfWeek(dt: DateTime, weekStartsOn: "sunday" | "monday"): DateTime {
  if (weekStartsOn === "monday") {
    return dt.startOf("week"); // luxon's default week starts Monday
  }
  // Sunday-start: shift back to the most recent Sunday
  const diff = dt.weekday % 7; // Sunday(7) -> 0, Monday(1) -> 1, ... Saturday(6) -> 6
  return dt.minus({ days: diff });
}

export const presetGroups: DatePresetGroup[] = [
  {
    key: "today",
    label: lang("Hoy", "Today"),
    presets: [
      {
        key: "today",
        label: lang("Hoy", "Today"),
        getRange: () => {
          const t = today();
          return range(t, t);
        },
      },
    ],
  },
  {
    key: "yesterday",
    label: lang("Ayer", "Yesterday"),
    presets: [
      {
        key: "yesterday",
        label: lang("Ayer", "Yesterday"),
        getRange: () => {
          const y = today().minus({ days: 1 });
          return range(y, y);
        },
      },
    ],
  },
  {
    key: "this-week",
    label: lang("Esta semana", "This week"),
    presets: [
      {
        key: "this-week-sun",
        label: lang(
          "Esta semana (inicia domingo)",
          "This week (starts Sunday)"
        ),
        getRange: () => {
          const t = today();
          const start = startOfWeek(t, "sunday");
          return range(start, start.plus({ days: 6 }));
        },
      },
      {
        key: "this-week-sun-to-date",
        label: lang(
          "Esta semana a la fecha (inicia domingo)",
          "This week to date (starts Sunday)"
        ),
        getRange: () => {
          const t = today();
          const start = startOfWeek(t, "sunday");
          return range(start, t);
        },
      },
      {
        key: "this-week-mon",
        label: lang(
          "Esta semana (inicia lunes)",
          "This week (starts Monday)"
        ),
        getRange: () => {
          const t = today();
          const start = startOfWeek(t, "monday");
          return range(start, start.plus({ days: 6 }));
        },
      },
      {
        key: "this-week-mon-to-date",
        label: lang(
          "Esta semana a la fecha (inicia lunes)",
          "This week to date (starts Monday)"
        ),
        getRange: () => {
          const t = today();
          const start = startOfWeek(t, "monday");
          return range(start, t);
        },
      },
    ],
  },
  {
    key: "this-month",
    label: lang("Este mes", "This month"),
    presets: [
      {
        key: "this-month",
        label: lang("Este mes", "This month"),
        getRange: () => {
          const t = today();
          return range(t.startOf("month"), t.endOf("month").startOf("day"));
        },
      },
      {
        key: "this-month-to-date",
        label: lang("Este mes a la fecha", "This month to date"),
        getRange: () => {
          const t = today();
          return range(t.startOf("month"), t);
        },
      },
    ],
  },
  {
    key: "last-7-days",
    label: lang("Últimos 7 días", "Last 7 days"),
    presets: [
      {
        key: "last-7-days",
        label: lang("Últimos 7 días", "Last 7 days"),
        getRange: () => {
          const t = today();
          return range(t.minus({ days: 6 }), t);
        },
      },
      {
        key: "last-14-days",
        label: lang("Últimos 14 días", "Last 14 days"),
        getRange: () => {
          const t = today();
          return range(t.minus({ days: 13 }), t);
        },
      },
      {
        key: "last-28-days",
        label: lang("Últimos 28 días", "Last 28 days"),
        getRange: () => {
          const t = today();
          return range(t.minus({ days: 27 }), t);
        },
      },
      {
        key: "last-30-days",
        label: lang("Últimos 30 días", "Last 30 days"),
        getRange: () => {
          const t = today();
          return range(t.minus({ days: 29 }), t);
        },
      },
      {
        key: "last-week-sun",
        label: lang(
          "Semana pasada (inicia domingo)",
          "Last week (starts Sunday)"
        ),
        getRange: () => {
          const t = today();
          const start = startOfWeek(t, "sunday").minus({ weeks: 1 });
          return range(start, start.plus({ days: 6 }));
        },
      },
      {
        key: "last-week-mon",
        label: lang(
          "Semana pasada (inicia lunes)",
          "Last week (starts Monday)"
        ),
        getRange: () => {
          const t = today();
          const start = startOfWeek(t, "monday").minus({ weeks: 1 });
          return range(start, start.plus({ days: 6 }));
        },
      },
      {
        key: "last-month",
        label: lang("Mes pasado", "Last month"),
        getRange: () => {
          const t = today().minus({ months: 1 });
          return range(t.startOf("month"), t.endOf("month").startOf("day"));
        },
      },
      {
        key: "last-quarter",
        label: lang("Trimestre pasado", "Last quarter"),
        getRange: () => {
          const t = today().minus({ quarters: 1 });
          return range(t.startOf("quarter"), t.endOf("quarter").startOf("day"));
        },
      },
      {
        key: "last-year",
        label: lang("Año pasado", "Last year"),
        getRange: () => {
          const t = today().minus({ years: 1 });
          return range(t.startOf("year"), t.endOf("year").startOf("day"));
        },
      },
    ],
  },
  {
    key: "this-quarter",
    label: lang("Este trimestre", "This quarter"),
    presets: [
      {
        key: "this-quarter",
        label: lang("Este trimestre", "This quarter"),
        getRange: () => {
          const t = today();
          return range(t.startOf("quarter"), t.endOf("quarter").startOf("day"));
        },
      },
      {
        key: "this-quarter-to-date",
        label: lang("Este trimestre a la fecha", "This quarter to date"),
        getRange: () => {
          const t = today();
          return range(t.startOf("quarter"), t);
        },
      },
    ],
  },
  {
    key: "this-year",
    label: lang("Este año", "This year"),
    presets: [
      {
        key: "this-year",
        label: lang("Este año", "This year"),
        getRange: () => {
          const t = today();
          return range(t.startOf("year"), t.endOf("year").startOf("day"));
        },
      },
      {
        key: "this-year-to-date",
        label: lang("Este año a la fecha", "This year to date"),
        getRange: () => {
          const t = today();
          return range(t.startOf("year"), t);
        },
      },
    ],
  },
];

export const STORAGE_KEY = "sales_date_range";

export type StoredDateRange = {
  start: string;
  end: string;
};

export function saveDateRangeToStorage(start: Date, end: Date): void {
  try {
    const value: StoredDateRange = {
      start: DateTime.fromJSDate(start).toISODate(),
      end: DateTime.fromJSDate(end).toISODate(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // localStorage unavailable — ignore
  }
}

export function loadDateRangeFromStorage(): [Date, Date] | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: StoredDateRange = JSON.parse(raw);
    const start = DateTime.fromISO(parsed.start);
    const end = DateTime.fromISO(parsed.end);
    if (!start.isValid || !end.isValid) return null;
    return [start.toJSDate(), end.toJSDate()];
  } catch {
    return null;
  }
}
